// Inline "add item" form rendered above the items table. Defaults to a
// pipe row with qty 1 — the user fills in description + size + length and
// hits Add. POSTs to /:id/items and the parent appends the returned row.
import { useState, type FormEvent } from "react";
import { Plus } from "lucide-react";
import { api } from "../../../lib/api";
import type { TakeoffItemDto } from "../../../../../shared/types/pipe-takeoff";

interface Props {
  projectId: number;
  onAdded: (item: TakeoffItemDto) => void;
}

const DEFAULT = {
  category: "pipe" as TakeoffItemDto["category"],
  description: "",
  sizeDn: "",
  material: "",
  quantity: 1,
  lengthM: "",
  sectionId: "",
};

const CATEGORIES = [
  "pipe",
  "fitting",
  "flange",
  "roll_grooved",
  "valve",
  "specialty",
  "air_bottle",
  "stud_drain",
] as const;

export function ItemAddForm({ projectId, onAdded }: Props) {
  const [draft, setDraft] = useState(DEFAULT);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const item = await api<TakeoffItemDto>(
        `/api/tools/pipe-takeoff/projects/${projectId}/items`,
        {
          method: "POST",
          json: {
            category: draft.category,
            description: draft.description,
            sizeDn: draft.sizeDn || undefined,
            material: draft.material || undefined,
            quantity: draft.quantity,
            lengthM: draft.lengthM === "" ? undefined : Number(draft.lengthM),
            sectionId: draft.sectionId || undefined,
          },
        },
      );
      onAdded(item);
      setDraft(DEFAULT);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} style={{ display: "grid", gap: 8 }}>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "end" }}>
        <select className="bsp-input" style={{ height: 30, fontSize: 12, width: 130 }}
          value={draft.category}
          onChange={(e) => setDraft({ ...draft, category: e.target.value as TakeoffItemDto["category"] })}>
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <input className="bsp-input" style={{ height: 30, flex: 1, minWidth: 200 }}
          placeholder="Description" required value={draft.description}
          onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
        <input className="bsp-input bsp-mono" style={{ height: 30, width: 80 }}
          placeholder="DN" value={draft.sizeDn}
          onChange={(e) => setDraft({ ...draft, sizeDn: e.target.value })} />
        <input className="bsp-input" style={{ height: 30, width: 110 }}
          placeholder="Material" value={draft.material}
          onChange={(e) => setDraft({ ...draft, material: e.target.value })} />
        <input className="bsp-input bsp-mono" style={{ height: 30, width: 70 }}
          type="number" min={1} step={1} placeholder="Qty" value={draft.quantity}
          onChange={(e) => setDraft({ ...draft, quantity: Number(e.target.value) })} />
        <input className="bsp-input bsp-mono" style={{ height: 30, width: 80 }}
          type="number" min={0} step={0.01} placeholder="Length (m)" value={draft.lengthM}
          onChange={(e) => setDraft({ ...draft, lengthM: e.target.value })} />
        <input className="bsp-input bsp-mono" style={{ height: 30, width: 90 }}
          placeholder="Section" value={draft.sectionId}
          onChange={(e) => setDraft({ ...draft, sectionId: e.target.value })} />
        <button type="submit" className="bsp-btn bsp-btn-primary bsp-btn-sm" disabled={busy}>
          <Plus size={14} style={{ marginRight: 4 }} />
          Add
        </button>
      </div>
      {error && (
        <p style={{ color: "var(--bsp-red-fg)", margin: 0, fontSize: 12 }}>{error}</p>
      )}
    </form>
  );
}
